import React, { useState, useEffect, useCallback } from 'react';
import { Box } from '@mui/material';
import { FREQUENCIES } from './FrequencySelector';
import PatternSection from './PatternSection';
import PeriodSection, { END_TYPES } from './PeriodSection';
import PreviewSection from './PreviewSection';
import { DAILY_MODES } from './patterns/DailyPattern';

/**
 * Parse an existing rrule string into component state
 */
const WORKDAY_DAYS = ['MO', 'TU', 'WE', 'TH', 'FR'];

const parseRRule = (rruleString) => {
  const defaults = {
    frequency: FREQUENCIES.WEEKLY,
    interval: 1,
    byWeekday: [],
    byMonthDay: 1,
    byMonth: 1,
    endType: END_TYPES.NEVER,
    count: 5,
    until: null,
    dailyMode: DAILY_MODES.INTERVAL,
  };

  if (!rruleString) return defaults;

  const parts = rruleString.split(';');
  const parsed = { ...defaults };

  parts.forEach((part) => {
    const [key, value] = part.split('=');
    switch (key) {
      case 'FREQ':
        if (Object.values(FREQUENCIES).includes(value)) {
          parsed.frequency = value;
        }
        break;
      case 'INTERVAL':
        parsed.interval = parseInt(value) || 1;
        break;
      case 'BYDAY':
        parsed.byWeekday = value.split(',');
        break;
      case 'BYMONTHDAY':
        parsed.byMonthDay = parseInt(value) || 1;
        break;
      case 'BYMONTH':
        parsed.byMonth = parseInt(value) || 1;
        break;
      case 'COUNT':
        parsed.endType = END_TYPES.COUNT;
        parsed.count = parseInt(value) || 5;
        break;
      case 'UNTIL':
        parsed.endType = END_TYPES.UNTIL;
        // Parse UNTIL date (format: YYYYMMDD or YYYYMMDDTHHMMSSZ)
        const dateStr = value.replace(/T.*$/, '');
        if (dateStr.length === 8) {
          const year = parseInt(dateStr.substring(0, 4));
          const month = parseInt(dateStr.substring(4, 6)) - 1;
          const day = parseInt(dateStr.substring(6, 8));
          parsed.until = new Date(year, month, day);
        }
        break;
    }
  });

  // Detect workday mode: FREQ=WEEKLY with BYDAY=MO,TU,WE,TH,FR and interval=1
  if (
    parsed.frequency === FREQUENCIES.WEEKLY &&
    parsed.interval === 1 &&
    parsed.byWeekday.length === 5 &&
    WORKDAY_DAYS.every(day => parsed.byWeekday.includes(day))
  ) {
    parsed.frequency = FREQUENCIES.DAILY;
    parsed.dailyMode = DAILY_MODES.WORKDAYS;
    parsed.byWeekday = [];
  }

  return parsed;
};

/**
 * Build an rrule string from component state
 */
const buildRRule = (state) => {
  // Handle workday mode: use FREQ=WEEKLY with BYDAY=MO,TU,WE,TH,FR
  if (state.frequency === FREQUENCIES.DAILY && state.dailyMode === DAILY_MODES.WORKDAYS) {
    const parts = ['FREQ=WEEKLY', `BYDAY=${WORKDAY_DAYS.join(',')}`];

    // End condition
    if (state.endType === END_TYPES.COUNT) {
      parts.push(`COUNT=${state.count}`);
    } else if (state.endType === END_TYPES.UNTIL && state.until) {
      const d = state.until;
      const dateStr = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
      parts.push(`UNTIL=${dateStr}`);
    }

    return parts.join(';');
  }

  const parts = [`FREQ=${state.frequency}`];

  if (state.interval > 1) {
    parts.push(`INTERVAL=${state.interval}`);
  }

  // Frequency-specific parts
  if (state.frequency === FREQUENCIES.WEEKLY && state.byWeekday.length > 0) {
    parts.push(`BYDAY=${state.byWeekday.join(',')}`);
  }

  if (state.frequency === FREQUENCIES.MONTHLY) {
    parts.push(`BYMONTHDAY=${state.byMonthDay}`);
  }

  if (state.frequency === FREQUENCIES.YEARLY) {
    parts.push(`BYMONTH=${state.byMonth}`);
    parts.push(`BYMONTHDAY=${state.byMonthDay}`);
  }

  // End condition
  if (state.endType === END_TYPES.COUNT) {
    parts.push(`COUNT=${state.count}`);
  } else if (state.endType === END_TYPES.UNTIL && state.until) {
    const d = state.until;
    const dateStr = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
    parts.push(`UNTIL=${dateStr}`);
  }

  return parts.join(';');
};

export default function RRuleInput({ rrule, setRrule }) {
  // Parse initial rrule or use defaults
  const [state, setState] = useState(() => parseRRule(rrule));

  // Update parent when state changes
  const updateRRule = useCallback((newState) => {
    setState(newState);
    const newRRule = buildRRule(newState);
    setRrule(newRRule);
  }, [setRrule]);

  // Sync from parent if rrule prop changes externally
  useEffect(() => {
    const parsed = parseRRule(rrule);
    const currentRRule = buildRRule(state);
    if (rrule !== currentRRule) {
      setState(parsed);
    }
  }, [rrule]);

  // Handlers
  const handleFrequencyChange = (frequency) => {
    const newState = { ...state, frequency };
    // Reset frequency-specific fields when changing frequency
    if (frequency === FREQUENCIES.WEEKLY) {
      newState.byWeekday = [];
    }
    if (frequency === FREQUENCIES.DAILY) {
      newState.dailyMode = DAILY_MODES.INTERVAL;
    }
    updateRRule(newState);
  };

  const handleDailyModeChange = (dailyMode) => {
    updateRRule({ ...state, dailyMode });
  };

  const handleIntervalChange = (interval) => {
    updateRRule({ ...state, interval });
  };

  const handleByWeekdayChange = (byWeekday) => {
    updateRRule({ ...state, byWeekday });
  };

  const handleByMonthDayChange = (byMonthDay) => {
    updateRRule({ ...state, byMonthDay });
  };

  const handleByMonthChange = (byMonth) => {
    updateRRule({ ...state, byMonth });
  };

  const handleEndTypeChange = (endType) => {
    updateRRule({ ...state, endType });
  };

  const handleCountChange = (count) => {
    updateRRule({ ...state, count });
  };

  const handleUntilChange = (until) => {
    updateRRule({ ...state, until });
  };

  const currentRRule = buildRRule(state);

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      {/* Pattern Section (includes Frequency Selector) */}
      <PatternSection
        frequency={state.frequency}
        onFrequencyChange={handleFrequencyChange}
        interval={state.interval}
        onIntervalChange={handleIntervalChange}
        byWeekday={state.byWeekday}
        onByWeekdayChange={handleByWeekdayChange}
        byMonthDay={state.byMonthDay}
        onByMonthDayChange={handleByMonthDayChange}
        byMonth={state.byMonth}
        onByMonthChange={handleByMonthChange}
        dailyMode={state.dailyMode}
        onDailyModeChange={handleDailyModeChange}
      />

      {/* Period Section */}
      <PeriodSection
        endType={state.endType}
        onEndTypeChange={handleEndTypeChange}
        count={state.count}
        onCountChange={handleCountChange}
        until={state.until}
        onUntilChange={handleUntilChange}
      />

      {/* Preview Section */}
      <PreviewSection rrule={currentRRule} />
    </Box>
  );
}
